import { Grid } from '@mui/material';
import { SparkDialog, SparkUISlider } from 'sharedComponents/UI';
import AttachmentCard from './AttachmentCard';
import ChatPromptInputField from './ChatPromptInputField';
import React from 'react';
import { SliderItem } from 'sharedComponents/UI/SparkUISlider';
import { useDialog } from 'hooks/common';
import { FileRejection } from 'react-dropzone';
import type { AttachmentCardType } from './AttachmentCard';

type SparkChatBubbleInnerProps = {
  openExpandedView?: () => void;
  expandable?: boolean;
  attachments?: AttachmentCardType[];
  openDialog?: () => void;
  onAttachmentRemove?: (
    cardType: AttachmentCardType['cardType'],
    id: AttachmentCardType['id']
  ) => void;
  onFileUpload: (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => Promise<(string | undefined)[] | undefined>;
  onSendMessage: (value: string) => void;
  onAddAttachments: (attachments: AttachmentCardType) => void;
  chatID?: string;
};

function SparkChatBubbleInner({
  expandable = true,
  attachments,
  onAttachmentRemove,
  onFileUpload,
  onSendMessage,
  openExpandedView,
  onAddAttachments,
  chatID,
}: SparkChatBubbleInnerProps) {
  const [selectedImage, setSelectedImage] = React.useState<SliderItem | null>(
    null
  );

  const {
    open: isImageDialogOpen,
    openDialog: openImageDialog,
    closeDialog: closeImageDialog,
  } = useDialog();

  const isDisabled = React.useMemo(() => {
    return attachments?.some((card) => card.isUploading);
  }, [attachments]);

  return (
    <Grid
      maxWidth='800px'
      container
      item
      bgcolor='#fff'
      flexDirection='column'
      borderRadius='20px'
      paddingX={1}
      sx={{ pointerEvents: 'all' }}
      border={(theme) => `1px solid ${theme.palette.borderColor}`}
    >
      {!!attachments?.length && (
        <Grid
          container
          item
          gap={2}
          paddingTop={2}
          paddingX={1}
          flexWrap='nowrap'
          sx={{ overflowX: 'auto', scrollbarWidth: 'none' }}
        >
          {attachments.map((card, idx) => (
            <AttachmentCard
              key={idx}
              cardType={card.cardType}
              thumbnail={card.thumbnail}
              fileTypeTag={card.fileTypeTag}
              file={card.file}
              title={card.title || ''}
              isUploading={card.isUploading}
              details={card.details}
              maxWidth='fit-content'
              minWidth={attachments.length < 4 ? 'fit-content' : '120px'}
              iconComponent={card.iconComponent}
              onClick={() => {
                if (card.file?.type.startsWith('image/')) {
                  setSelectedImage({
                    type: 'image',
                    url: card.thumbnail || card.url || '',
                    alt: card.thumbnail,
                  });
                  openImageDialog();
                }
              }}
              onClose={() => {
                onAttachmentRemove &&
                  onAttachmentRemove(card.cardType, card.id);
              }}
            />
          ))}
        </Grid>
      )}
      <ChatPromptInputField
        openDialog={openExpandedView}
        handleFileUpload={onFileUpload}
        expandable={expandable}
        onSendMessage={onSendMessage}
        disabled={isDisabled}
        onAddAttachments={onAddAttachments}
        isUploadingFiles={attachments?.some((card) => card.isUploading)}
        chatID={chatID}
      />
      {isImageDialogOpen && selectedImage && (
        <SparkDialog
          open={isImageDialogOpen}
          onClose={closeImageDialog}
          title=''
          sx={{
            '& .MuiPaper-root': {
              borderRadius: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          }}
          hideTitle={true}
          fullScreen={true}
        >
          <SparkUISlider sliderItems={[selectedImage]} />
        </SparkDialog>
      )}
    </Grid>
  );
}

export default React.memo(SparkChatBubbleInner);
